/**
 * The import's copy (2.I-2 deliverable 7), pure: one sentence per refusal code, mapping warning,
 * import warning, `foundry_import_state` reason and identity reason. No refusal, warning or error
 * sentence takes a file, actor or page name: an error is read beside the card it is about.
 */
import {
  type ActorImportStatus,
  FOUNDRY_IMPORT_STATE_REASONS,
  FOUNDRY_REFUSAL_CODES,
  type FoundryImportStateReason,
  type FoundryRefusalCode,
  type IdentityReason,
  ACTOR_IMPORT_WARNING_CODES as IMPORT_WARNING_CODES,
} from '@codex/contracts';
import { MAPPING_WARNING_CODES } from '@codex/dnd5e/foundry';
import { IDENTITY_REASON_TEXT } from '@codex/ui';

export { IMPORT_WARNING_CODES, MAPPING_WARNING_CODES };

type MappingWarningCode = (typeof MAPPING_WARNING_CODES)[number];
type ImportWarningCode = (typeof IMPORT_WARNING_CODES)[number];

export const REFUSAL_TEXT: Readonly<Record<FoundryRefusalCode, string>> = {
  not_json: "This file isn't JSON, so Codex couldn't read it.",
  json_limits: 'This file is too large or too deeply nested for Codex to read.',
  not_an_actor: "This file doesn't hold a Foundry actor.",
  unsupported_system: "This actor isn't from Foundry's D&D 5e system.",
  reserved_layout: 'This actor comes from a newer dnd5e version than Codex reads yet.',
  unsupported_layout: "This actor's dnd5e version is one Codex doesn't read.",
  unsupported_actor_type: 'Codex imports player characters and NPCs only.',
  schema_invalid: "The Codex macro's file is damaged; run the macro again.",
};

export const MAPPING_WARNING_TEXT: Readonly<Record<MappingWarningCode, string>> = {
  rules_2024_content: 'Some of this actor uses the 2024 rules; the sheet stays on the 2014 rules.',
  world_rules_modern: 'The Foundry world is set to the 2024 rules; Codex reads it as 2014.',
  stamp_older_than_layout:
    "The file's version stamp is older than its contents; Codex read the contents.",
  unknown_condition: "A condition isn't one Codex knows; it was left out.",
  unknown_damage_type: "A damage type isn't one Codex knows; it was left out.",
  currency_unknown_rate: "A currency isn't one Codex knows; it was left out of the totals.",
  item_type_unmapped: 'An item of a kind Codex does not map was kept as a plain item.',
  item_id_invalid: 'An item had an id Codex could not use; it was given a new one.',
  activity_unmapped: "An item's activity isn't mapped; its description is kept.",
  description_sanitized: 'Some formatting in a description was removed for safety.',
  value_invalid: "A value couldn't be read and was left empty.",
  schema_issue: "Part of the actor didn't match what Codex expects; it was left out.",
  input_too_deep: 'Part of the actor is nested too deeply; it was left out.',
  input_too_large: 'Part of the actor is too large; it was left out.',
};

export const IMPORT_WARNING_TEXT: Readonly<Record<ImportWarningCode, string>> = {
  older_than_applied: 'This file is older than the import last applied to this sheet.',
  already_applied: 'This same file was already applied.',
  world_mismatch: 'This file comes from another Foundry world.',
  biography_truncated: "Foundry's biography is very long; only its first part can be added.",
};

/** `foundry_import_state`'s reasons, as contracts declare them (CS r6b, DECISIONS 805.3 and 805.5). */
export const STATE_REASONS = FOUNDRY_IMPORT_STATE_REASONS;
export type StateReason = FoundryImportStateReason;

export const STATE_REASON_TEXT: Readonly<Record<StateReason, string>> = {
  sheet_target_unsupported:
    "Codex can't apply this file to an existing sheet; import it as new or choose another page.",
  link_not_unchanged:
    "This creature differs from its SRD entry, so it can't be linked; import it as your variant or a separate creature.",
  no_reference:
    "Codex has no SRD entry for this creature, so it can't be a variant; import it as a separate creature.",
  foundry_id_taken: 'Another page already holds this Foundry actor; choose it or import as new.',
  not_mappable: 'Codex can no longer read the stored file; drop it again to preview it.',
  refresh_unsupported_kind: "Codex can't refresh a stat block from Foundry yet.",
  not_newest: 'Only the newest import of this sheet can be undone.',
  changed_since: "The sheet or page changed after this import, so Codex can't undo it.",
};

/** A `foundry_import_state` answer without a reason: the status that refused the step. */
export const STATE_STATUS_TEXT: Readonly<Record<ActorImportStatus, string>> = {
  previewed: "This preview hasn't been applied yet.",
  applied: 'This import was already applied.',
  rejected: 'This preview was already rejected.',
  undone: 'This import was already undone.',
  refused: "Codex refused this file, so it can't be applied.",
};

export const IDENTITY_TEXT: Readonly<Record<IdentityReason, string>> = IDENTITY_REASON_TEXT;

export const UNKNOWN_CODE = (code: string): string =>
  `Codex noted something it can't describe yet (${code}).`;

const has = <K extends string>(table: Readonly<Record<K, string>>, code: string): code is K =>
  Object.hasOwn(table, code);

export function refusalText(code: string): string {
  return has(REFUSAL_TEXT, code) ? REFUSAL_TEXT[code] : UNKNOWN_CODE(code);
}

/** A stored warning: 2.G's mapping codes, then the import's own. */
export function warningText(code: string): string {
  if (has(MAPPING_WARNING_TEXT, code)) return MAPPING_WARNING_TEXT[code];
  if (has(IMPORT_WARNING_TEXT, code)) return IMPORT_WARNING_TEXT[code];
  return UNKNOWN_CODE(code);
}

/** A 409 `foundry_import_state`: its reason's sentence, else its status's. */
export function stateText(details: unknown): string {
  const d = (typeof details === 'object' && details !== null ? details : {}) as {
    reason?: unknown;
    status?: unknown;
  };
  if (typeof d.reason === 'string') {
    return has(STATE_REASON_TEXT, d.reason) ? STATE_REASON_TEXT[d.reason] : UNKNOWN_CODE(d.reason);
  }
  if (typeof d.status === 'string' && has(STATE_STATUS_TEXT, d.status)) {
    return STATE_STATUS_TEXT[d.status];
  }
  return UNKNOWN_CODE('foundry_import_state');
}

export const UPLOAD_TEXT = {
  tooLarge: 'That file is larger than 25 MB.',
  notJson: 'Choose a .json file exported from Foundry.',
  oneFile: 'Choose one file at a time.',
  failed: "Codex couldn't read that file.",
} as const;

/**
 * The dropzone's own refusal (UI r3's `FileRejection`): this package's sentence in place of the
 * kit's, which names the file. The rejection carries the name; the sentence never does.
 */
export function rejectionText(rejection: {
  reason: 'size' | 'type' | 'count';
  name: string;
}): string {
  if (rejection.reason === 'size') return UPLOAD_TEXT.tooLarge;
  if (rejection.reason === 'type') return UPLOAD_TEXT.notJson;
  return UPLOAD_TEXT.oneFile;
}

/** The upload's failure sentence by the answer's status. */
export function uploadErrorText(status: number | null): string {
  if (status === 413) return UPLOAD_TEXT.tooLarge;
  if (status === 415) return UPLOAD_TEXT.notJson;
  return UPLOAD_TEXT.failed;
}

export const CARD_TEXT = {
  rowConflict: 'This preview changed; check it again.',
  badChoice: "Codex couldn't use that choice.",
  failed: "Codex couldn't save that; try again.",
  rejected: 'Preview rejected.',
  proposed: 'Confirm the match, choose another page or import as new.',
  statBlock: "Codex can't refresh a stat block from Foundry yet.",
  snapshot: "Includes Foundry's own numbers from the Codex macro.",
  computed: 'Sheet numbers will be computed by Codex from the 2014 rules.',
  biography: "Foundry's biography stays in Foundry. After import you can add it to the page.",
  biographyEndsUndo: 'After this, Import history can no longer undo this import.',
  linked: 'Linked to the SRD entry',
  differs: 'It differs from the SRD entry.',
} as const;

export function moduleFlagLine(n: number): string {
  return n === 1
    ? '1 module flag kept in the original file'
    : `${n} module flags kept in the original file`;
}

/**
 * The refused card's mapper line (deliverable 8, "Nearest mapper: <mapper> <version>"): the
 * registry key already carries a dnd5e version range, so the mapper's own revision is labeled.
 */
export function nearestMapperLine(mapper: string, version: number): string {
  return `Nearest mapper: ${mapper} (mapper version ${version})`;
}

export const actorCount = (n: number): string => (n === 1 ? '1 actor' : `${n} actors`);

/** The heading of one file's previews: by this visit's answer, else by the stored file name. */
export function groupHeading(
  format: 'macro' | 'export' | 'unknown' | null,
  fileName: string,
  n: number,
): string {
  if (format === 'macro') return `Codex macro file · ${actorCount(n)}`;
  if (format === 'export') return 'Foundry Export Data';
  if (format === 'unknown') return 'Not a Foundry actor file';
  return `From ${fileName} · ${actorCount(n)}`;
}

export const PAGE_TEXT = {
  numbersHeading: "Foundry's own numbers",
  numbers:
    'Sheets show Foundry\'s exact numbers when you import the Codex macro\'s file. From a plain Export Data file, Codex computes them from the 2014 rules and marks them "computed".',
  dropLabel: 'Foundry files',
  dropHint: "Export Data files or the Codex macro's party file, up to 25 MB.",
  loading: 'Loading Foundry imports…',
  empty: 'Drop a file from Foundry to preview it. Nothing is written until you press Apply.',
  failed: "Foundry imports couldn't be loaded.",
  unknown: "This import doesn't exist.",
  off: 'Foundry import is turned off.',
} as const;

export const BIOGRAPHY_TEXT = {
  added: "Foundry's biography was added at the end of the page.",
  images: (n: number) =>
    n === 1 ? '1 image stayed in Foundry.' : `${n} images stayed in Foundry.`,
  banner: 'Biography added',
  noEditor: 'Open the page for editing to add the biography.',
  failed: "Foundry's biography couldn't be read.",
  heading: 'Foundry biography',
} as const;

export const SETTINGS_TEXT = {
  heading: 'Foundry',
  intro: 'Preview actors from Foundry VTT and import them as pages with sheets.',
  world: (id: string) => `Codex expects files from the Foundry world ${id}.`,
  noWorld: 'No Foundry world saved yet.',
  forgotten: 'Codex will accept the next world it sees.',
  forgetFailed: "Codex couldn't forget the saved world; try again.",
} as const;

/** Every code the three lists hold, for the copy spec. */
export const ALL_CODES = {
  refusal: FOUNDRY_REFUSAL_CODES,
  mapping: MAPPING_WARNING_CODES,
  import: IMPORT_WARNING_CODES,
} as const;

/** The refresh view (stage s2, deliverable 14). */
export const REFRESH_TEXT = {
  heading: 'Changes since the last import from Foundry',
  defaults:
    'Codex keeps your value where you edited something Foundry also changed, and keeps items you edited that Foundry removed.',
  renamed: (name: string) => `Foundry's name is now ${name}; the page title stays as it is.`,
  table: (title: string) => `Refresh ${title} from Foundry`,
  empty: 'Nothing changed in Foundry since the last import.',
  done: 'Refreshed from Foundry.',
  rowConflict: 'The character sheet changed while you were reading; check the changes again.',
  referenceReadOnly: "This sheet is in the reference library, so Codex can't refresh it.",
  deletedHere: 'Deleted here',
  sheetFailed: "The character sheet couldn't be loaded.",
} as const;

/** Undo, from the refresh banner and Import history (deliverable 15). */
export const UNDO_TEXT = {
  done: 'Import undone.',
  confirmNew: (title: string) => `Undo this import? The page ${title} moves to the trash.`,
  confirmDocument: (title: string) =>
    `Undo this import? The character sheet leaves the page ${title}; the page stays.`,
  title: 'Undo this import',
  failed: "Codex couldn't undo that; try again.",
} as const;

/** Import history (deliverable 17). */
export const HISTORY_TEXT = {
  heading: 'Import history',
  loading: 'Loading import history…',
  empty: 'No imports yet.',
  failed: "Import history couldn't be loaded.",
  linked: 'Linked to the SRD entry',
} as const;

/** The status filter's words, the GM's "Waiting" for `previewed`. */
export const STATUS_LABEL: Readonly<Record<ActorImportStatus, string>> = {
  applied: 'Applied',
  previewed: 'Waiting',
  rejected: 'Rejected',
  undone: 'Undone',
  refused: 'Refused',
};

/** The pill and the import page's refresh banner (deliverable 16). */
export const PILL_TEXT = {
  pill: 'Refresh from Foundry',
  refreshing: (title: string) =>
    `Refreshing ${title}: drop the new Foundry file for this character.`,
  noMatch: (title: string) =>
    `No actor in that file matched ${title}; use Choose another on its card.`,
} as const;

/** The macro's download (deliverable 18). */
export const MACRO_TEXT = {
  link: 'Download the Codex macro',
  how: "Paste it into a Foundry script macro and run it with your party's tokens selected.",
  file: 'codex-export.js',
} as const;
